import { supabase } from '../supabase';
import { BOOKING_STATUS_TRIGGER } from './triggers';
import { generateFeedbackForBooking, generateFeedbackURL, isFeedbackEnabled } from '../feedback';

// Debug flag to control logging - set to false to disable verbose logs
const enableDirectTriggerDebug = false;

/**
 * Directly trigger booking status automations from the UI
 * This bypasses the database change detection mechanism and directly executes automations
 */
export async function directTriggerBookingStatus(bookingId: string, newStatus: string, businessId: string) {
  // Always log critical actions, even when debug is off
  console.log(`[DIRECT TRIGGER] 🚀 Triggering booking status automations for status: ${newStatus}`);
  
  if (!supabase) {
    console.error('[DIRECT TRIGGER] ❌ Supabase client not initialized');
    return { success: false, error: 'Supabase client not initialized' };
  }

  try {
    // Find automations configured for this status and business
    const { data: automations, error } = await supabase
      .from('automation_settings')
      .select('*')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .eq('booking_status_active', true);
    
    if (error) {
      console.error('[DIRECT TRIGGER] ❌ Error fetching automations:', error);
      return { success: false, error };
    }
    
    // Filter automations to those that match the new status
    const matchingAutomations = automations?.filter(automation => 
      Array.isArray(automation.booking_status_trigger_statuses) && 
      automation.booking_status_trigger_statuses.includes(newStatus)
    ) || [];
    
    // Always log number of matching automations as it's important
    console.log(`[DIRECT TRIGGER] 📋 Found ${matchingAutomations.length} automations for status: ${newStatus}`);
    
    if (matchingAutomations.length === 0) {
      if (enableDirectTriggerDebug) console.log('[DIRECT TRIGGER] ℹ️ No matching automations found');
      return { success: true, results: [] };
    }
    
    // Fetch base booking information first (without services)
    const { data: booking, error: bookingError } = await supabase
      .from('bookings')
      .select(`
        id,
        customer_id,
        business_id,
        status,
        drop_off_date,
        drop_off_time,
        start_date,
        start_time,
        booking_number,
        title,
        businesses:business_id (
          id,
          name
        ),
        customers:customer_id (
          id,
          first_name,
          last_name,
          email,
          phone
        )
      `)
      .eq('id', bookingId)
      .single();
    
    if (bookingError || !booking) {
      console.error('[DIRECT TRIGGER] ❌ Error fetching booking details:', bookingError || 'Booking not found');
      return { success: false, error: bookingError || 'Booking not found' };
    }
    
    // Now fetch booking services separately
    const { data: services, error: servicesError } = await supabase
      .from('booking_services')
      .select(`
        id,
        service_id,
        vehicle_id,
        name,
        description,
        price,
        tax_exempt,
        tax_rate,
        tax_amount,
        total_amount,
        duration_minutes
      `)
      .eq('booking_id', bookingId);
    
    if (servicesError) {
      console.error('[DIRECT TRIGGER] ❌ Error fetching booking services:', servicesError);
      // Don't return error here, just proceed with empty services array
    } else {
      console.log(`[DIRECT TRIGGER] 📋 Found ${services?.length || 0} services for booking ${bookingId}`);
      if (services && services.length > 0) {
        console.log('[DIRECT TRIGGER] 📋 First service sample:', services[0]);
      }
    }
    
    // Now try to get vehicle information from the vehicles table using vehicle_id from the first service
    let vehicle_make = '';
    let vehicle_model = '';
    let vehicle_year = '';
    let vehicle_color = '';
    let service_name = 'Appointment'; // Default value
    
    // Get service name if available
    if (services && services.length > 0) {
      service_name = services[0].name || 'Appointment';
      
      // Now try to get vehicle info if we have a vehicle_id
      if (services[0].vehicle_id) {
        try {
          console.log(`[DIRECT TRIGGER] 🔍 Looking up vehicle with ID: ${services[0].vehicle_id}`);
          
          // Try to get vehicle info from the vehicles table
          const { data: vehicleData, error: vehicleError } = await supabase
            .from('customer_vehicles')
            .select(`
              id,
              make,
              model,
              year,
              color
            `)
            .eq('id', services[0].vehicle_id)
            .single();
            
          if (vehicleError) {
            console.error('[DIRECT TRIGGER] ❌ Error fetching vehicle data:', vehicleError);
          } else if (vehicleData) {
            console.log('[DIRECT TRIGGER] ✅ Found vehicle data:', vehicleData);
            
            // Extract vehicle information
            vehicle_make = vehicleData.make || '';
            vehicle_model = vehicleData.model || '';
            vehicle_year = vehicleData.year ? vehicleData.year.toString() : '';
            vehicle_color = vehicleData.color || '';
          } else {
            console.log('[DIRECT TRIGGER] ⚠️ No vehicle data found for vehicle_id:', services[0].vehicle_id);
          }
        } catch (vehicleLookupError) {
          console.error('[DIRECT TRIGGER] ❌ Error during vehicle lookup:', vehicleLookupError);
        }
      } else {
        console.log('[DIRECT TRIGGER] ⚠️ No vehicle_id found in service');
      }
    } else {
      console.log('[DIRECT TRIGGER] ⚠️ No services found for booking - using empty vehicle information');
    }
    
    // Add services to the booking object
    const bookingWithServices = {
      ...booking,
      services: services || []
    };
    
    if (enableDirectTriggerDebug) {
      console.log('[DIRECT TRIGGER] ✅ Loaded booking details');
      console.log('[DIRECT TRIGGER] 📋 Found services:', services?.length || 0);
    }
    
    // Import necessary actions from actions.ts
    const { SMS_ACTION, EMAIL_ACTION, NOTIFY_PERSON_ACTION, WAIT_ACTION } = await import('./actions');
    
    // Prepare template data for actions (similar to what BookingStatusProcessor uses)
    // Format booking data as a similar format used by the processor
    const businesses = Array.isArray(booking.businesses) ? booking.businesses[0] : booking.businesses;
    const customers = Array.isArray(booking.customers) ? booking.customers[0] : booking.customers;
    
    // Helper function to format time in AM/PM format
    const formatTimeAMPM = (time: string): string => {
      if (!time) return '';
      
      // Parse the time string (assuming format like "14:30:00")
      const [hours, minutes] = time.split(':').map(Number);
      
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
      
      return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
    };
    
    // Create template data for SMS/email variables
    const templateData: Record<string, string> = {
      customer_first_name: customers.first_name || '',
      customer_last_name: customers.last_name || '',
      customer_full_name: `${customers.first_name || ''} ${customers.last_name || ''}`.trim(),
      drop_off_date_formatted: booking.drop_off_date 
        ? new Date(booking.drop_off_date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC'
          })
        : '',
      drop_off_time_formatted: formatTimeAMPM(booking.drop_off_time || ''),
      start_date_formatted: booking.start_date 
        ? new Date(booking.start_date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC'
          })
        : '',
      start_time_formatted: formatTimeAMPM(booking.start_time || ''),
      business_name: businesses.name || '',
      appointment_id: booking.id,
      booking_id: booking.id,
      booking_number: booking.booking_number || booking.id.split('-')[0] || booking.id,
      booking_title: booking.title || 'Untitled Booking',
      service_name,
      vehicle_make,
      vehicle_model,
      vehicle_year,
      vehicle_color,
      vehicle: (vehicle_make && vehicle_model) ? 
        `${vehicle_year ? vehicle_year + ' ' : ''}${vehicle_make} ${vehicle_model}${vehicle_color ? ' (' + vehicle_color + ')' : ''}` : 
        'No vehicle information',
      phoneNumber: customers.phone || '',
      email: customers.email || '',
      drop_off_date: booking.drop_off_date || '',
      drop_off_time: booking.drop_off_time || '',
      status: newStatus,
      status_formatted: newStatus === 'in_progress' ? 'In Progress' : 
                         newStatus === 'completed' ? 'Completed' : 
                         newStatus.charAt(0).toUpperCase() + newStatus.slice(1),
      status_date: new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
      }),
      status_time: new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        timeZone: 'UTC'
      })
    };
    
    // Generate feedback link for completed bookings BEFORE processing automations
    let feedbackLink = '';
    if (newStatus === 'completed') {
      try {
        // Check if feedback is enabled for this business
        const feedbackIsEnabled = await isFeedbackEnabled(booking.business_id);
        
        if (feedbackIsEnabled) {
          if (enableDirectTriggerDebug) {
            console.log(`[DIRECT TRIGGER] 📝 Generating feedback link for completed booking: ${bookingId}`);
            console.log(`[DIRECT TRIGGER] 🔍 Business ID: ${booking.business_id}`);
            console.log(`[DIRECT TRIGGER] 🔍 Customer info:`, {
              name: `${customers.first_name} ${customers.last_name}`,
              email: customers.email,
              phone: customers.phone
            });
          }
          
          const feedbackToken = await generateFeedbackForBooking(bookingId);
          
          if (feedbackToken) {
            feedbackLink = generateFeedbackURL(feedbackToken);
            console.log(`[DIRECT TRIGGER] ✅ Feedback link generated successfully: ${feedbackLink}`);
            
            // Store the feedback link in the bookings table
            try {
              const { error: updateError } = await supabase
                .from('bookings')
                .update({ feedback_link: feedbackLink })
                .eq('id', bookingId);
              
              if (updateError) {
                console.error(`[DIRECT TRIGGER] ❌ Failed to store feedback link in bookings table:`, updateError);
              } else {
                console.log(`[DIRECT TRIGGER] ✅ Feedback link stored in bookings table successfully`);
              }
            } catch (error) {
              console.error(`[DIRECT TRIGGER] ❌ Error storing feedback link in bookings table:`, error);
            }
          } else {
            console.error(`[DIRECT TRIGGER] ❌ Failed to generate feedback token for booking: ${bookingId}`);
          }
        } else {
          console.log(`[DIRECT TRIGGER] 🔒 Feedback disabled for business ${booking.business_id}, skipping feedback generation`);
        }
      } catch (error) {
        console.error(`[DIRECT TRIGGER] ❌ Error generating feedback link:`, error);
      }
    }

    // Add feedback link to template data
    templateData.feedback_link = feedbackLink;
    templateData.feedback_url = feedbackLink; // Also add as feedback_url for backward compatibility
    
    if (enableDirectTriggerDebug) {
      console.log(`[DIRECT TRIGGER] 🔗 Template data feedback_link: ${feedbackLink ? 'Generated' : 'Empty'}`);
      console.log(`[DIRECT TRIGGER] 🔗 Final feedback_link value: "${feedbackLink}"`);
    }

    // IMPORTANT: Add delay for completed bookings to ensure feedback token is fully committed
    // This prevents race condition where SMS sends before feedback link is available
    if (newStatus === 'completed' && feedbackLink) {
      console.log(`[DIRECT TRIGGER] ⏱️ Adding 5-second delay to ensure feedback token is committed to database...`);
      await new Promise(resolve => setTimeout(resolve, 5000));
      console.log(`[DIRECT TRIGGER] ✅ Delay completed, proceeding with automation processing`);
    }
    
    // Process each automation
    const results = [];
    
    for (const automation of matchingAutomations) {
      if (enableDirectTriggerDebug) console.log(`[DIRECT TRIGGER] 🔄 Processing automation: ${automation.title}`);
      
      // Process SMS action
      if (automation.sms_action_active && customers.phone) {
        try {
          // Set the message on the action instance
          SMS_ACTION.settings = {
            ...SMS_ACTION.settings,
            message: automation.sms_action_message || ''
          };
          
          if (enableDirectTriggerDebug) console.log(`[DIRECT TRIGGER] 📱 Sending SMS to customer: ${customers.phone}`);
          
          // Execute the SMS action
          await SMS_ACTION.execute(booking.business_id, templateData);
          
          // Log the action
          await supabase.from('automation_action_log').insert({
            business_id: booking.business_id,
            automation_id: automation.id,
            action_type: 'sms',
            recipient: customers.phone,
            trigger_type: 'booking_status',
            trigger_data: {
              booking_id: booking.id,
              status: newStatus,
              timestamp: new Date().toISOString(),
              vehicle_make,
              vehicle_model,
              vehicle_year,
              vehicle_color,
              service_name
            }
          });
          
          results.push({ type: 'sms', automation_id: automation.id, success: true });
        } catch (error) {
          console.error('[DIRECT TRIGGER] ❌ SMS action failed:', error);
          results.push({ type: 'sms', automation_id: automation.id, success: false, error });
        }
      }
      
      // Process Email action
      if (automation.email_action_active && customers.email) {
        try {
          // Set the subject and body on the action instance
          EMAIL_ACTION.settings = {
            ...EMAIL_ACTION.settings,
            subject: automation.email_action_subject || '',
            emailBody: automation.email_action_body || ''
          };
          
          if (enableDirectTriggerDebug) console.log(`[DIRECT TRIGGER] 📧 Sending email to customer: ${customers.email}`);
          
          // Execute the Email action
          await EMAIL_ACTION.execute(booking.business_id, templateData);
          
          // Log the action
          await supabase.from('automation_action_log').insert({
            business_id: booking.business_id,
            automation_id: automation.id,
            action_type: 'email',
            recipient: customers.email,
            trigger_type: 'booking_status',
            trigger_data: {
              booking_id: booking.id,
              status: newStatus,
              timestamp: new Date().toISOString(),
              vehicle_make,
              vehicle_model,
              vehicle_year,
              vehicle_color,
              service_name
            }
          });
          
          results.push({ type: 'email', automation_id: automation.id, success: true });
        } catch (error) {
          console.error('[DIRECT TRIGGER] ❌ Email action failed:', error);
          results.push({ type: 'email', automation_id: automation.id, success: false, error });
        }
      }
      
      // Process Notify Person action
      if (automation.notify_person_active) {
        try {
          // Add detailed logging about the notify person settings only in debug mode
          if (enableDirectTriggerDebug) {
            console.log('[DIRECT TRIGGER] 🔍 Notify Person action details:');
            console.log(`[DIRECT TRIGGER] 📋 Target type: ${automation.notify_person_target || 'Not set (default=admin)'}`);
            console.log(`[DIRECT TRIGGER] 📋 Custom phone number: ${automation.notify_person_phone || 'Not set'}`);
            console.log(`[DIRECT TRIGGER] 📋 Message template length: ${(automation.notify_person_message || '').length} chars`);
          }
          
          // Set the notify settings
          NOTIFY_PERSON_ACTION.settings = {
            ...NOTIFY_PERSON_ACTION.settings,
            notifyTarget: automation.notify_person_target || 'admin',
            notifyPhoneNumber: automation.notify_person_phone || '',
            notifyMessage: automation.notify_person_message || ''
          };
          
          // Always log this important notification action
          console.log(`[DIRECT TRIGGER] 🔔 Sending notification to ${automation.notify_person_target || 'admin'}`);
          
          // Execute the notify person action
          await NOTIFY_PERSON_ACTION.execute(booking.business_id, templateData);
          
          results.push({ type: 'notify_person', automation_id: automation.id, success: true });
        } catch (error) {
          console.error('[DIRECT TRIGGER] ❌ Notify person action failed:', error);
          results.push({ type: 'notify_person', automation_id: automation.id, success: false, error });
        }
      }
      
      // Process Wait action
      if (automation.wait_action_active) {
        try {
          // Set the wait settings
          WAIT_ACTION.settings = {
            ...WAIT_ACTION.settings,
            waitDuration: automation.wait_action_duration || 30,
            waitUnit: (automation.wait_action_unit as 'minutes' | 'hours' | 'days') || 'minutes'
          };
          
          console.log(`[DIRECT TRIGGER] ⏱️ Executing wait action: ${automation.wait_action_duration} ${automation.wait_action_unit}`);
          
          // Execute the wait action
          await WAIT_ACTION.execute(booking.business_id, templateData);
          
          results.push({ type: 'wait', automation_id: automation.id, success: true });
        } catch (error) {
          console.error('[DIRECT TRIGGER] ❌ Wait action failed:', error);
          results.push({ type: 'wait', automation_id: automation.id, success: false, error });
        }
      }
    }
    
    // Always log the final result
    console.log(`[DIRECT TRIGGER] ✅ Successfully processed ${results.length} actions for status ${newStatus}`);
    
    return { success: true, results };
  } catch (error) {
    console.error('[DIRECT TRIGGER] ❌ Error processing direct trigger:', error);
    return { success: false, error };
  }
} 